import { nextTick, onBeforeMount, onMounted, watch } from 'vue'
import { booleanCookieSerializer } from '~/utils/cookie-serializers'
import {
  resolveSidebarCollapsed,
  resolveSidebarUserPref,
  routeWantsCompact,
  syncSidebarCollapsedDomAttr,
  SIDEBAR_COLLAPSED_COOKIE,
  SIDEBAR_COOKIE_OPTIONS,
  SIDEBAR_STATE_KEY,
  SIDEBAR_USER_PREF_COOKIE,
  SIDEBAR_WATCHES_KEY,
  writeSidebarUserPrefClient,
} from '~/utils/dashboard-sidebar-state'

/**
 * 侧栏用户偏好（sidebarCollapsed + cookie）。初始值由 `sidebar-state-init` 写入。
 * 壳层 UI 与 DOM 请用 `useDashboardShellLayout().navCollapsed`，勿在此重复公式。
 *
 * @see docs/dashboard-shell-state.md
 */
function effectiveSidebarUserPref(sidebarUserPref) {
  const cookieHeader = import.meta.client && typeof document !== 'undefined'
    ? document.cookie
    : ''
  const pref = resolveSidebarUserPref(cookieHeader, sidebarUserPref.value)
  if (pref && sidebarUserPref.value !== pref) {
    sidebarUserPref.value = pref
  }
  return pref || sidebarUserPref.value
}

function syncCollapsedState(route, sidebarUserPref, sidebarCollapsed) {
  const pref = effectiveSidebarUserPref(sidebarUserPref)
  const resolved = resolveSidebarCollapsed(route, pref)
  sidebarCollapsed.value = resolved
  if (import.meta.client) {
    syncSidebarCollapsedDomAttr(resolved)
  }
}

export function useDashboardSidebarCollapsed() {
  const route = useRoute()

  const sidebarCollapsedCookie = useCookie(SIDEBAR_COLLAPSED_COOKIE, {
    default: () => false,
    ...SIDEBAR_COOKIE_OPTIONS,
    watch: true,
    serializer: booleanCookieSerializer,
  })

  const sidebarUserPref = useCookie(SIDEBAR_USER_PREF_COOKIE, {
    default: () => '',
    ...SIDEBAR_COOKIE_OPTIONS,
    watch: true,
  })

  const sidebarCollapsed = useState(SIDEBAR_STATE_KEY, () =>
    resolveSidebarCollapsed(route, sidebarUserPref.value),
  )

  syncCollapsedState(route, sidebarUserPref, sidebarCollapsed)

  const watchesInstalled = useState(SIDEBAR_WATCHES_KEY, () => false)

  if (!watchesInstalled.value) {
    watchesInstalled.value = true

    if (import.meta.client) {
      watch(
        sidebarCollapsed,
        (value) => {
          sidebarCollapsedCookie.value = value
          syncSidebarCollapsedDomAttr(value)
        },
      )

      watch(sidebarUserPref, () => {
        syncCollapsedState(route, sidebarUserPref, sidebarCollapsed)
      })
    }

    watch(
      () => route.fullPath,
      () => {
        const pref = sidebarUserPref.value
        if (pref === 'collapsed' || pref === 'expanded') return
        syncCollapsedState(route, sidebarUserPref, sidebarCollapsed)
      },
    )

    if (import.meta.client) {
      onBeforeMount(() => {
        syncCollapsedState(route, sidebarUserPref, sidebarCollapsed)
      })
      onMounted(() => {
        syncCollapsedState(route, sidebarUserPref, sidebarCollapsed)
      })
    }
  }

  function routeWantsCollapsed(targetRoute = route) {
    return routeWantsCompact(targetRoute)
  }

  function applySidebarForRoute(targetRoute = route) {
    syncCollapsedState(targetRoute, sidebarUserPref, sidebarCollapsed)
  }

  function toggleSidebar() {
    const next = !sidebarCollapsed.value
    const pref = next ? 'collapsed' : 'expanded'
    if (import.meta.client) {
      writeSidebarUserPrefClient(pref)
    }
    sidebarUserPref.value = pref
    sidebarCollapsed.value = next
    if (import.meta.client) {
      sidebarCollapsedCookie.value = next
      syncSidebarCollapsedDomAttr(next)
      nextTick(() => syncSidebarCollapsedDomAttr(sidebarCollapsed.value))
    }
  }

  return {
    sidebarCollapsed,
    sidebarUserPref,
    toggleSidebar,
    applySidebarForRoute,
    routeWantsCollapsed,
  }
}
